import { bekannteAdresse, bildVon, useAvatare } from './avatarBilder.js'

/**
 * Runder Avatar — mit Profilbild, sonst mit Initialen.
 *
 * Kein Bild von außen — auch kein „Platzhalter-Dienst". Ein Avatar, der von
 * einem fremden Server käme, wäre eine Meldung nach außen bei jedem Blick in
 * die Familienliste, und genau das soll diese App nicht tun. Das Profilbild
 * kommt aus dem eigenen Blob-Speicher (Zone B, Migration 0018).
 *
 * Der Kreis ist `aria-hidden`: Direkt daneben steht der Name im Klartext.
 * Ein Screenreader, der erst „L E" und dann „Lena" vorliest, ist langsamer,
 * nicht barrierefreier.
 */

/**
 * Höchstens zwei Buchstaben — „Anna-Maria Schmidt" wird zu „AS".
 *
 * Nimmt bewusst auch `null` und `undefined` an. Ein Raumtitel ist im
 * Datenmodell `string | null` (bei Direktchats steht der Name nicht am Raum,
 * sondern ergibt sich aus den Mitgliedern), und ein Avatar, der die App zum
 * Absturz bringt, weil ein Name fehlt, ist schlimmer als ein Fragezeichen.
 * Genau das ist beim Umbau passiert: Die Anrufliste warf, sobald ein
 * Zweiergespräch darin stand.
 */
export function initialen(name: string | null | undefined): string {
  const teile = (name ?? '').trim().split(/[\s-]+/).filter(Boolean)
  if (teile.length === 0) return '?'
  const erste = teile[0]![0] ?? ''
  const letzte = teile.length > 1 ? teile[teile.length - 1]![0] ?? '' : ''
  return (erste + letzte).toUpperCase()
}

export function Avatar({ name, gross, symbol, person }: {
  name: string | null | undefined
  gross?: boolean
  /** Statt Initialen ein Symbol — für den Familienchat, der keine Person ist. */
  symbol?: string | undefined
  /**
   * Wessen Bild. Die Zuordnung Person → Bild liegt zentral in
   * `avatarBilder.ts` — so muss nicht jede Datenquelle ein eigenes Feld
   * mitschleppen und keine kann es vergessen.
   */
  person?: string | null | undefined
}) {
  // Der Zähler wird nicht gelesen — er sorgt nur dafür, dass dieser Avatar neu
  // gezeichnet wird, sobald die Bilder geladen sind.
  useAvatare()
  const adresse = bekannteAdresse(bildVon(person))

  return (
    <span class={`fh-avatar${gross ? ' fh-avatar--gross' : ''}`} aria-hidden="true">
      {adresse
        ? (
          /*
           * `onError` ist kein Zierrat: Die Adresse ist signiert und läuft ab.
           * Ohne diesen Rückfall bliebe ein leerer Kreis stehen — mit ihm
           * stehen wieder die Initialen da, wie vor dem Bild.
           */
          <img
            class="fh-avatar__bild"
            src={adresse}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => { (e.currentTarget).style.display = 'none' }}
          />
        )
        : symbol ?? initialen(name)}
    </span>
  )
}
